import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { trackFunnelEvent } from "@/services/FunnelAnalytics";

const heroBackground = "/site-assets/hero-background.jpg";
const heroBackgroundMobile = "/site-assets/hero-family-mobile.jpg";
const heroSourcePhoto = "/site-assets/source-photo.jpg";

const socialProof = [
  { value: "16+", label: "styles artistiques" },
  { value: "29,99 €", label: "prix de départ" },
  { value: "5–7 j", label: "livraison France estimée" },
];

const intentChips = [
  { label: "Offrir un cadeau", source: "hero_intent_gift" },
  { label: "Portrait animal", source: "hero_intent_pet" },
  { label: "Déco maison", source: "hero_intent_home_decor" },
  { label: "Famille ou couple", source: "hero_intent_family_couple" },
] as const;

const HeroSection = () => {
  const navigate = useNavigate();

  const startCreateFlow = (source: string) => {
    trackFunnelEvent("homepage_cta_clicked", { source });
    navigate(`/create?source=${source}`);
  };

  return (
    <section className="section-shell gradient-hero relative flex min-h-screen items-center overflow-hidden pt-24">
      <div className="hero-orb left-[8%] top-28 h-32 w-32 bg-accent/40" />
      <div className="hero-orb right-[10%] top-20 h-40 w-40 bg-primary/30 [animation-delay:2s]" />
      <div className="hero-orb bottom-24 right-[24%] h-28 w-28 bg-secondary/30 [animation-delay:4s]" />

      <div className="container relative mx-auto px-6 pb-16 pt-14">
        <div className="grid items-center gap-14 lg:grid-cols-[1.05fr_0.95fr]">
          <div className="max-w-2xl">
            <p className="reveal-fade mb-4 inline-flex rounded-full border border-primary/15 bg-white/55 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-primary/85 shadow-card backdrop-blur-sm">
              Photo IA imprimée sur tableau
            </p>

            <h1 className="display-title reveal-fade mb-6 max-w-3xl text-foreground">
              Transformez une photo favorite en <span className="inline-block text-premium">tableau personnalisé</span>.
            </h1>

            <p className="reveal-fade-delay mb-5 max-w-xl text-lg leading-8 text-foreground/72 md:text-xl">
              Importez un portrait de famille, de couple ou d'animal, choisissez un style artistique, puis recevez une impression premium prête à offrir ou à accrocher.
            </p>

            <div className="reveal-fade-delay mb-8 rounded-[1.6rem] border border-white/65 bg-white/55 p-4 shadow-card backdrop-blur-sm">
              <p className="mb-3 text-sm font-semibold text-foreground/72">
                Démarrez par votre intention : vous importez la photo, explorez les styles et supports, puis vous validez seulement au moment de commander.
              </p>
              <div className="flex flex-wrap gap-2.5">
                {intentChips.map((intent) => (
                  <button
                    key={intent.source}
                    type="button"
                    className="rounded-full border border-foreground/10 bg-white/70 px-3.5 py-2 text-sm font-semibold text-foreground/68 shadow-card transition-smooth hover:-translate-y-0.5 hover:border-primary/25 hover:text-primary"
                    onClick={() => startCreateFlow(intent.source)}
                  >
                    {intent.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="reveal-fade-delay flex flex-col items-start gap-4 sm:flex-row sm:items-center">
              <Button
                variant="premium"
                size="lg"
                className="h-14 rounded-full px-8 text-base shadow-glow"
                onClick={() => startCreateFlow("hero_primary")}
              >
                Créer mon œuvre
                <ArrowRight className="h-4 w-4" />
              </Button>
              <button
                className="group inline-flex items-center gap-2 rounded-full border border-foreground/12 bg-white/55 px-5 py-3 text-sm font-semibold text-foreground/72 panel-glass transition-premium hover:-translate-y-0.5 hover:text-foreground"
                onClick={() => {
                  trackFunnelEvent("homepage_cta_clicked", { source: "hero_styles_scroll" });
                  document.getElementById("gallery")?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                Découvrir nos styles
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>

            <div className="reveal-fade-delay mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {socialProof.map((item) => (
                <div key={item.label} className="rounded-[1.6rem] border border-white/60 bg-white/60 px-5 py-4 shadow-card backdrop-blur-md">
                  <div className="text-lg font-extrabold text-foreground md:text-2xl">{item.value}</div>
                  <div className="mt-1 text-sm text-foreground/58">{item.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="reveal-fade-delay relative flex justify-center lg:justify-end">
            <div className="relative w-full max-w-[35rem]">
              <div className="absolute inset-6 rounded-[2.2rem] bg-white/24 blur-3xl" />

              <div className="relative overflow-hidden rounded-[2.2rem] border border-white/65 bg-white/36 p-3.5 shadow-luxury backdrop-blur-xl">
                <div className="overflow-hidden rounded-[1.65rem] bg-[#f7efe8] shadow-[inset_0_1px_0_rgba(255,255,255,0.82)]">
                  <picture>
                    <source media="(max-width: 767px)" srcSet={heroBackgroundMobile} />
                    <img
                      src={heroBackground}
                      alt="Tableau aquarelle personnalisé à partir de la photo de la même famille — Ma Photo en Tableau"
                      width={700}
                      height={560}
                      className="aspect-[4/5] w-full object-cover object-center md:aspect-[5/4] md:object-[55%_43%]"
                    />
                  </picture>
                </div>
              </div>

              <div className="absolute -bottom-3 -left-5 z-10 sm:-bottom-4 sm:-left-6">
                <div className="relative h-[14.5rem] w-[6.95rem] rounded-[2rem] bg-[#1a1a1a] p-[0.32rem] shadow-[0_24px_46px_rgba(38,24,13,0.26)] sm:h-[16rem] sm:w-[7.65rem]">
                  <div className="absolute left-0 top-[3.15rem] h-10 w-[0.18rem] rounded-r-full bg-black/20 sm:top-[3.5rem]" />
                  <div className="absolute left-0 top-[4.35rem] h-12 w-[0.18rem] rounded-r-full bg-black/20 sm:top-[4.85rem]" />
                  <div className="absolute right-0 top-[4rem] h-16 w-[0.18rem] rounded-l-full bg-black/20 sm:top-[4.45rem]" />

                  <div className="relative h-full w-full overflow-hidden rounded-[1.7rem] bg-black">
                    <img
                      src={heroSourcePhoto}
                      alt="Photo de famille originale sur smartphone transformée en tableau"
                      className="h-full w-full object-cover object-center"
                    />
                    <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,255,255,0.1),rgba(255,255,255,0.015)_16%,transparent_30%,transparent)]" />
                    <div className="absolute left-1/2 top-2.5 h-5 w-14 -translate-x-1/2 rounded-full bg-black/88 shadow-[inset_0_1px_0_rgba(255,255,255,0.08)] sm:top-3 sm:w-[3.8rem]" />
                    <div className="absolute right-3 top-3 h-2 w-2 rounded-full bg-white/18 sm:top-3.5" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
